// Advanced Geometry Text Analyzer Service
// This service provides enhanced capabilities for analyzing geometric text descriptions
// and converting them into structured geometric data for SVG generation

class GeometryTextAnalyzer {
  constructor() {
    this.geometricPatterns = {
      // Point patterns
      points: [
        /([A-Z])\s*\(([^)]+)\)/gi,  // A(2,6)
        /point\s+([A-Z])\s*\(([^)]+)\)/gi,  // point A(2,6)
        /([A-Z])\s+at\s*\(([^)]+)\)/gi,  // A at (2,6)
        /([A-Z])\s+coordinates\s*\(([^)]+)\)/gi,  // A coordinates (2,6)
        /([A-Z])\s+located\s+at\s*\(([^)]+)\)/gi,  // A located at (2,6)
        /△([A-Z][A-Z][A-Z])/gi,  // △ABC
        /([A-Z])\s+and\s+([A-Z])\s+intersect/gi,  // A and B intersect
        /([A-Z])\s+meets\s+([A-Z])/gi,  // A meets B
        /([A-Z])\s+intersects\s+([A-Z])/gi,  // A intersects B
        /([A-Z])\s+at\s+([A-Z])/gi,  // A at B
        /([A-Z])\s+is\s+the\s+intersection/gi,  // A is the intersection
        /intersect\s+at\s+([A-Z])/gi,  // intersect at A
        /([A-Z])\s+is\s+located\s+on/gi,  // A is located on
        /([A-Z])\s+lies\s+on/gi,  // A lies on
        /([A-Z])\s+is\s+on/gi,  // A is on
      ],
      
      // Line patterns
      lines: [
        /([A-Z][A-Z])/gi,  // AB
        /([A-Z][A-Z])\s*,\s*([A-Z][A-Z])/gi,  // AB, CD
        /([A-Z][A-Z])\s+parallel\s+([A-Z][A-Z])/gi,  // AB parallel CD
        /([A-Z][A-Z])\s+perpendicular\s+([A-Z][A-Z])/gi,  // AB perpendicular CD
        /([A-Z][A-Z])\s*∥\s*([A-Z][A-Z])/gi,  // AB ∥ CD
        /([A-Z][A-Z])\s*⊥\s*([A-Z][A-Z])/gi,  // AB ⊥ CD
        /([A-Z][A-Z])\s+intersects\s+([A-Z][A-Z])/gi,  // AB intersects CD
        /([A-Z][A-Z])\s+meets\s+([A-Z][A-Z])/gi,  // AB meets CD
        /([A-Z][A-Z])\s+and\s+([A-Z][A-Z])/gi,  // AB and CD
        /([A-Z][A-Z])\s+with\s+([A-Z][A-Z])/gi,  // AB with CD
        /([A-Z][A-Z])\s+through\s+([A-Z])/gi,  // AB through C
        /([A-Z][A-Z])\s+from\s+([A-Z])\s+to\s+([A-Z])/gi,  // AB from A to B
        /([A-Z][A-Z])\s+connects\s+([A-Z])\s+and\s+([A-Z])/gi,  // AB connects A and B
        /([A-Z][A-Z])\s+joins\s+([A-Z])\s+and\s+([A-Z])/gi,  // AB joins A and B
        /([A-Z][A-Z])\s+between\s+([A-Z])\s+and\s+([A-Z])/gi,  // AB between A and B
      ],
      
      // Extension patterns
      extensions: [
        /extension\s+of\s+([A-Z][A-Z])/gi,
        /([A-Z][A-Z])\s+extended/gi,
        /([A-Z][A-Z])\s+and\s+([A-Z][A-Z])\s+extensions/gi,
        /([A-Z][A-Z])\s+produced/gi,
        /([A-Z][A-Z])\s+extended\s+to\s+([A-Z])/gi,
        /([A-Z][A-Z])\s+meets\s+([A-Z][A-Z])\s+extension/gi,
        /([A-Z][A-Z])\s+intersects\s+([A-Z][A-Z])\s+extension/gi,
        /([A-Z][A-Z])\s+extended\s+beyond\s+([A-Z])/gi,
        /([A-Z][A-Z])\s+produced\s+to\s+([A-Z])/gi,
      ],
      
      // Angle patterns
      angles: [
        /∠([A-Z][A-Z][A-Z])\s*=\s*(\d+)°/gi,  // ∠ABC = 45°
        /angle\s+([A-Z][A-Z][A-Z])\s*=\s*(\d+)°/gi,  // angle ABC = 45°
        /([A-Z][A-Z][A-Z])\s*=\s*(\d+)°/gi,  // ABC = 45°
        /([A-Z])\s+angle\s*=\s*(\d+)°/gi,  // A angle = 45°
        /right\s+angle\s+at\s+([A-Z])/gi,  // right angle at A
        /([A-Z])\s+is\s+a\s+right\s+angle/gi,  // A is a right angle
        /([A-Z])\s+is\s+90°/gi,  // A is 90°
        /([A-Z])\s*=\s*90°/gi,  // A = 90°
      ],
      
      // Measurement patterns
      measurements: [
        /([A-Z][A-Z])\s*=\s*(\d+)\s*cm/gi,  // AB = 5 cm
        /([A-Z][A-Z])\s*=\s*(\d+)\s*units/gi,  // AB = 5 units
        /([A-Z][A-Z])\s*=\s*(\d+)/gi,  // AB = 5
        /length\s+of\s+([A-Z][A-Z])\s*=\s*(\d+)/gi,  // length of AB = 5
        /([A-Z][A-Z])\s+has\s+length\s+(\d+)/gi,  // AB has length 5
        /([A-Z][A-Z])\s+is\s+(\d+)\s*cm/gi,  // AB is 5 cm
        /([A-Z][A-Z])\s+is\s+(\d+)\s*units/gi,  // AB is 5 units
        /([A-Z][A-Z])\s+is\s+(\d+)/gi,  // AB is 5
      ],
      
      // Circle patterns
      circles: [
        /circle\s+with\s+center\s+([A-Z])/gi,  // circle with center O
        /center\s+([A-Z])/gi,  // center O
        /radius\s*=\s*(\d+)/gi,  // radius = 5
        /radius\s+(\d+)/gi,  // radius 5
        /diameter\s+([A-Z][A-Z])/gi,  // diameter AB
        /chord\s+([A-Z][A-Z])/gi,  // chord AB
        /([A-Z][A-Z])\s+is\s+a\s+diameter/gi,  // AB is a diameter
        /([A-Z][A-Z])\s+is\s+a\s+chord/gi,  // AB is a chord
        /([A-Z][A-Z])\s+is\s+a\s+radius/gi,  // AB is a radius
      ],
      
      // Coordinate patterns
      coordinates: [
        /\(([^)]+)\)/gi,  // (2,6)
        /coordinates?\s*\(([^)]+)\)/gi,  // coordinates (2,6)
        /at\s*\(([^)]+)\)/gi,  // at (2,6)
        /located\s+at\s*\(([^)]+)\)/gi,  // located at (2,6)
        /position\s*\(([^)]+)\)/gi,  // position (2,6)
      ]
    };
    
    this.geometricKeywords = {
      shapes: ['triangle', 'circle', 'square', 'rectangle', 'quadrilateral', 'pentagon', 'hexagon', 'polygon'],
      relationships: ['parallel', 'perpendicular', 'intersect', 'meet', 'connect', 'join'],
      properties: ['similar', 'congruent', 'equal', 'right', 'acute', 'obtuse'],
      measurements: ['length', 'width', 'height', 'area', 'perimeter', 'radius', 'diameter'],
      positions: ['center', 'vertex', 'midpoint', 'intersection', 'endpoint']
    };
  }

  // Analyze text and extract geometric information
  analyzeText(text) {
    const analysis = {
      points: new Set(),
      lines: [],
      extensions: [],
      angles: [],
      measurements: [],
      circles: [],
      coordinates: [],
      keywords: [],
      shape: null,
      description: ''
    };

    const lowerText = text.toLowerCase();

    // Extract points
    this.geometricPatterns.points.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const letters = match.match(/[A-Z]/g);
          if (letters) {
            letters.forEach(letter => analysis.points.add(letter));
          }
        });
      }
    });

    // Extract lines
    this.geometricPatterns.lines.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const lineMatches = match.match(/[A-Z][A-Z]/g);
          if (lineMatches) {
            lineMatches.forEach(line => {
              const from = line[0];
              const to = line[1];
              if (analysis.points.has(from) && analysis.points.has(to)) {
                const lineInfo = this.analyzeLine(line, lowerText);
                analysis.lines.push(lineInfo);
              }
            });
          }
        });
      }
    });

    // Extract extensions
    this.geometricPatterns.extensions.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const extensionMatches = match.match(/[A-Z][A-Z]/g);
          if (extensionMatches) {
            extensionMatches.forEach(line => {
              const from = line[0];
              const to = line[1];
              if (analysis.points.has(from) && analysis.points.has(to)) {
                analysis.extensions.push({
                  from,
                  to,
                  color: 'gray',
                  width: 0.67,
                  strokeDasharray: '5,5'
                });
              }
            });
          }
        });
      }
    });

    // Extract angles
    this.geometricPatterns.angles.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const angleMatch = match.match(/[A-Z][A-Z][A-Z]/);
          const measureMatch = match.match(/(\d+)/);
          if (angleMatch && measureMatch) {
            analysis.angles.push({
              vertex: angleMatch[0][1],
              points: [angleMatch[0][0], angleMatch[0][2]],
              measure: parseInt(measureMatch[1])
            });
          }
        });
      }
    });

    // Extract measurements
    this.geometricPatterns.measurements.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const lineMatch = match.match(/[A-Z][A-Z]/);
          const measureMatch = match.match(/(\d+)/);
          if (lineMatch && measureMatch) {
            analysis.measurements.push({
              line: lineMatch[0],
              value: parseInt(measureMatch[1]),
              unit: match.includes('cm') ? 'cm' : 'units'
            });
          }
        });
      }
    });

    // Extract circles
    this.geometricPatterns.circles.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const centerMatch = match.match(/[A-Z]/);
          const radiusMatch = match.match(/(\d+)/);
          if (centerMatch) {
            analysis.circles.push({
              center: centerMatch[0],
              radius: radiusMatch ? parseInt(radiusMatch[1]) : 50
            });
          }
        });
      }
    });

    // Extract coordinates
    this.geometricPatterns.coordinates.forEach(pattern => {
      const matches = lowerText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const coordMatch = match.match(/\(([^)]+)\)/);
          if (coordMatch) {
            const coords = coordMatch[1].split(',').map(c => c.trim());
            if (coords.length === 2) {
              analysis.coordinates.push({
                x: parseFloat(coords[0]),
                y: parseFloat(coords[1])
              });
            }
          }
        });
      }
    });

    // Extract keywords
    Object.entries(this.geometricKeywords).forEach(([category, keywords]) => {
      keywords.forEach(keyword => {
        if (lowerText.includes(keyword)) {
          analysis.keywords.push({ category, keyword });
        }
      });
    });

    // Determine shape
    analysis.shape = this.determineShape(lowerText);

    // Generate description
    analysis.description = this.generateDescription(analysis);

    return analysis;
  }

  // Analyze a specific line and determine its properties
  analyzeLine(line, text) {
    let color = 'blue';
    let width = 1;
    let strokeDasharray = 'none';

    if (text.includes('diameter') && line.includes('A') && line.includes('C')) {
      color = 'red';
      width = 1.33;
    } else if (text.includes('chord')) {
      color = 'green';
      width = 1;
    } else if (text.includes('radius')) {
      color = 'orange';
      width = 1;
    } else if (text.includes('perpendicular') || text.includes('⊥')) {
      color = 'purple';
      width = 1.33;
    } else if (text.includes('parallel') || text.includes('∥')) {
      color = 'brown';
      width = 1;
    }

    return {
      from: line[0],
      to: line[1],
      color,
      width,
      strokeDasharray
    };
  }

  // Determine the main geometric shape from text
  determineShape(text) {
    if (text.includes('triangle') || text.includes('△')) return 'triangle';
    if (text.includes('circle') || text.includes('圓')) return 'circle';
    if (text.includes('square')) return 'square';
    if (text.includes('rectangle')) return 'rectangle';
    if (text.includes('quadrilateral')) return 'quadrilateral';
    if (text.includes('pentagon')) return 'pentagon';
    if (text.includes('hexagon')) return 'hexagon';
    if (text.includes('polygon')) return 'polygon';
    if (text.includes('line') || text.includes('perpendicular') || text.includes('parallel')) return 'lines';
    return 'mixed';
  }

  // Generate a comprehensive description from analysis
  generateDescription(analysis) {
    let description = '';

    if (analysis.shape) {
      description += `${analysis.shape.charAt(0).toUpperCase() + analysis.shape.slice(1)} `;
    }

    if (analysis.points.size > 0) {
      const points = Array.from(analysis.points).sort();
      description += `with points ${points.join(', ')} `;
    }

    if (analysis.lines.length > 0) {
      description += `and lines ${analysis.lines.map(l => `${l.from}${l.to}`).join(', ')} `;
    }

    if (analysis.extensions.length > 0) {
      description += `with extensions ${analysis.extensions.map(e => `${e.from}${e.to}`).join(', ')} `;
    }

    if (analysis.angles.length > 0) {
      description += `angles: ${analysis.angles.map(a => `∠${a.points.join('')} = ${a.measure}°`).join(', ')} `;
    }

    if (analysis.measurements.length > 0) {
      description += `measurements: ${analysis.measurements.map(m => `${m.line} = ${m.value}${m.unit}`).join(', ')} `;
    }

    if (analysis.circles.length > 0) {
      description += `circles: ${analysis.circles.map(c => `center ${c.center}, radius ${c.radius}`).join(', ')} `;
    }

    if (analysis.coordinates.length > 0) {
      description += `coordinates: ${analysis.coordinates.map(c => `(${c.x}, ${c.y})`).join(', ')} `;
    }

    return description.trim();
  }

  // Learn from examples to improve pattern recognition
  learnFromExample(text, expectedResult) {
    // This method can be used to improve pattern recognition based on examples
    // For now, it's a placeholder for future machine learning capabilities
    console.log('Learning from example:', { text, expectedResult });
  }

  // Get enhanced analysis with confidence scores
  getEnhancedAnalysis(text) {
    const basicAnalysis = this.analyzeText(text);
    
    // Add confidence scores based on pattern matches
    const confidence = {
      points: basicAnalysis.points.size > 0 ? 0.8 : 0.3,
      lines: basicAnalysis.lines.length > 0 ? 0.8 : 0.3,
      shape: basicAnalysis.shape !== 'mixed' ? 0.9 : 0.5,
      coordinates: basicAnalysis.coordinates.length > 0 ? 0.9 : 0.3,
      measurements: basicAnalysis.measurements.length > 0 ? 0.8 : 0.3
    };

    return {
      ...basicAnalysis,
      confidence,
      overallConfidence: Object.values(confidence).reduce((a, b) => a + b, 0) / Object.keys(confidence).length
    };
  }
}

export default new GeometryTextAnalyzer();
